/**
 * ExtractionPanel
 * Reads stored extraction_result from a linked document and presents
 * each extracted field one-by-one for review, editing, and applying to the form.
 *
 * States: idle → loading → revealing → ready
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  DocumentMagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../../services/api';
import { ENGAGEMENT_TYPES } from '../documents/DocumentManagementTab';


// ── Field definitions ─────────────────────────────────────────────────────
// Each entry maps an extraction key → form field + display metadata

const FORM_FIELDS = [
  {
    extractionKey: 'engagement_type',
    formField:     'engagementType',
    label:         'Engagement Type',
    type:          'select',
    options:       ENGAGEMENT_TYPES,
    getValue:      (e) => e.engagement_type,
  },
  {
    extractionKey: 'service_category',
    formField:     'practiceArea',
    label:         'Practice Area',
    type:          'text',
    getValue:      (e) => e.service_category,
  },
  {
    extractionKey: 'proposal_date',
    formField:     'bidDate',
    label:         'Bid / Proposal Date',
    type:          'date',
    getValue:      (e) => e.proposal_date,
  },
  {
    extractionKey: 'agreement_date',
    formField:     'decisionDate',
    label:         'Agreement / Decision Date',
    type:          'date',
    getValue:      (e) => e.agreement_date,
  },
  {
    extractionKey: 'start_date',
    formField:     'startDate',
    label:         'Start Date',
    type:          'date',
    getValue:      (e) => e.start_date,
  },
  {
    extractionKey: 'total_fee',
    formField:     'bidAmount',
    label:         'Bid Amount',
    type:          'currency',
    getValue:      (e) => e.total_fee != null ? String(e.total_fee) : null,
  },
  {
    extractionKey: 'scope_summary',
    formField:     'scopeSummary',
    label:         'Scope Summary',
    type:          'textarea',
    getValue:      (e) => e.scope_summary,
  },
];

// Display-only fields (informational, not mapped to form)
const INFO_FIELDS = [
  { key: 'client_name',    label: 'Client Name',     getValue: (e) => e.client_name },
  { key: 'fee_type',       label: 'Fee Type',        getValue: (e) => e.fee_type },
  { key: 'payment_terms',  label: 'Payment Terms',   getValue: (e) => e.payment_terms },
  { key: 'timeline',  label:    'Timeline',     getValue: (e) => e.timeline_description || (e.timeline_weeks ? `${e.timeline_weeks} weeks` : null),},
  { key: 'goals', label: 'Goals', getValue: (e) => e.goals },
];

// ── Helpers ───────────────────────────────────────────────────────────────
const fmt = (val) =>
  val
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
    : null;

const REVEAL_DELAY_MS = 180; // stagger between each field appearing

// ── Sub-components ────────────────────────────────────────────────────────

const FieldInput = ({ fieldDef, value, onChange }) => {
  const cls =
    'w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white ' +
    'focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors';

  if (fieldDef.type === 'textarea') {
    return (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className={cls}
      />
    );
  }
  if (fieldDef.type === 'select') {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={cls}>
        <option value="">Select...</option>
        {fieldDef.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (fieldDef.type === 'currency') {
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${cls} pl-7`}
        />
      </div>
    );
  }
  // date or text
  return (
    <input
      type={fieldDef.type === 'date' ? 'date' : 'text'}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={cls}
    />
  );
};

// Single extracted field row — animates in, shows accept/reject toggle + editable value
const ExtractedFieldRow = ({
  fieldDef,
  value,
  accepted,
  visible,
  onToggleAccept,
  onValueChange,
}) => {
  if (!visible) return null;

  return (
    <div
      className={`
        flex gap-3 p-3 rounded-lg border transition-all duration-300
        ${accepted
          ? 'bg-brand-50 border-brand-200'
          : 'bg-slate-50 border-grey-200 opacity-60'
        }
      `}
      style={{ animation: 'slideInField 0.25s ease-out' }}
    >
      {/* Accept toggle */}
      <button
        type="button"
        onClick={() => onToggleAccept(!accepted)}
        className="flex-shrink-0 mt-0.5"
        title={accepted ? 'Exclude this field' : 'Include this field'}
      >
        {accepted ? (
          <CheckCircleIcon className="w-5 h-5 text-brand-500" />
        ) : (
          <XCircleIcon className="w-5 h-5 text-slate-300" />
        )}
      </button>

      {/* Label + input */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold uppercase tracking-wide text-darkblue-500 mb-1.5">
          {fieldDef.label}
        </p>
        {accepted ? (
          <FieldInput fieldDef={fieldDef} value={value} onChange={onValueChange} />
        ) : (
          <p className="text-sm text-slate-400 line-through truncate">
            {fieldDef.type === 'currency' ? fmt(Number(value)) : (value || '—')}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────

const ExtractionPanel = ({ documentId, formData, onApply }) => {
  const [panelOpen, setPanelOpen]     = useState(false);
  const [status, setStatus]           = useState('idle'); // idle | loading | revealing | ready | error
  const [errorMsg, setErrorMsg]       = useState('');
  const [extractedAt, setExtractedAt] = useState(null);
  const [confidence, setConfidence]   = useState(null);

  // fieldStates: { [formField]: { value, accepted, visible } }
  const [fieldStates, setFieldStates] = useState({});
  // Info fields (display only)
  const [infoFields, setInfoFields]   = useState([]);
  // Tags
  const [extractedTags, setExtractedTags] = useState([]);

  const revealTimer = useRef(null);

  // Reset when document changes
  useEffect(() => {
    setStatus('idle');
    setFieldStates({});
    setInfoFields([]);
    setExtractedTags([]);
    setPanelOpen(false);
  }, [documentId]);

  // Cleanup on unmount
  useEffect(() => () => clearTimeout(revealTimer.current), []);

  const handleFetch = async () => {
    if (!documentId) return;
    setPanelOpen(true);
    setStatus('loading');
    setErrorMsg('');
    setFieldStates({});

    try {
      const response = await apiService.get(`/document-ai/${documentId}/extraction`);

      let extractionResult = response?.extraction_result;
      if (typeof extractionResult === 'string') {
        try { extractionResult = JSON.parse(extractionResult); } catch { extractionResult = null; }
      }

      if (!extractionResult || extractionResult.status === 'failed') {
        setStatus('error');
        setErrorMsg(extractionResult?.error || 'Extraction failed or not available for this document.');
        return;
      }

      const extraction = extractionResult.extraction;
      if (!extraction) {
        setStatus('error');
        setErrorMsg('No extraction data found. Try re-processing the document from Document Management.');
        return;
      }

      setExtractedAt(extractionResult.extracted_at || null);
      setConfidence(extractionResult.confidence_score ?? null);

      // Build field states — pre-checked if field is empty in the form
      const initial = {};
      FORM_FIELDS.forEach((fd) => {
        const extracted = fd.getValue(extraction);
        if (extracted == null) return; // nothing extracted for this field
        const currentVal = formData[fd.formField];
        const isEmpty = !currentVal || currentVal === '';
        initial[fd.formField] = {
          value:    String(extracted),
          accepted: isEmpty, // auto-accept if form field is empty
          visible:  false,
        };
      });

      // Tags
      const tags = extractionResult.tags?.tags || [];
      setExtractedTags(tags);

      // Info fields
      const info = INFO_FIELDS
        .map((f) => ({ ...f, value: f.getValue(extraction) }))
        .filter((f) => f.value);
      setInfoFields(info);

      // Stagger reveal
      setStatus('revealing');
      const keys = Object.keys(initial);
      keys.forEach((key, idx) => {
        setTimeout(() => {
          setFieldStates((prev) => ({
            ...prev,
            [key]: { ...initial[key], visible: true },
          }));
          if (idx === keys.length - 1) {
            setTimeout(() => setStatus('ready'), REVEAL_DELAY_MS);
          }
        }, idx * REVEAL_DELAY_MS);
      });

      // If nothing extracted
      if (keys.length === 0) {
        setStatus('ready');
        setFieldStates({});
      }

    } catch (err) {
      console.error('ExtractionPanel fetch failed:', err);
      setStatus('error');
      setErrorMsg('Failed to read extraction data. Please try again.');
    }
  };

  const updateFieldValue = (formField, value) => {
    setFieldStates((prev) => ({
      ...prev,
      [formField]: { ...prev[formField], value },
    }));
  };

  const toggleAccept = (formField, accepted) => {
    setFieldStates((prev) => ({
      ...prev,
      [formField]: { ...prev[formField], accepted },
    }));
  };

  const handleApply = () => {
    
    const updates = {};
    Object.entries(fieldStates).forEach(([formField, state]) => {
      if (state.accepted && state.value != null) {
        updates[formField] = state.value;
      }
    });

    // Tags: merge accepted extracted tags with existing
    if (extractedTags.length > 0) {
      const existing = Array.isArray(formData.tags) ? formData.tags : [];
      updates.tags = [...new Set([...existing, ...extractedTags])];
    }
    console.log("updates", updates)
    onApply(updates);
    setPanelOpen(false);
    setStatus('idle');
  };
  
  const acceptedCount = Object.values(fieldStates).filter((f) => f.accepted).length;
  const visibleCount  = Object.values(fieldStates).filter((f) => f.visible).length;
  const totalCount    = Object.keys(fieldStates).length;

  return (
    <>
      {/* Keyframe for field slide-in */}
      <style>{`
        @keyframes slideInField {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Trigger button */}
      <button
        type="button"
        onClick={panelOpen && status !== 'idle' ? () => setPanelOpen((p) => !p) : handleFetch}
        className="flex items-center gap-2 px-3 py-2 text-xs font-semibold bg-brand-50 text-brand-500 border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors"
      >
        <SparklesIcon className="w-3.5 h-3.5" />
        {status === 'idle' || !panelOpen ? 'Read Extracted Data' : panelOpen ? 'Hide Panel' : 'Show Panel'}
        {panelOpen
          ? <ChevronUpIcon className="w-3 h-3" />
          : <ChevronDownIcon className="w-3 h-3" />
        }
      </button>

      {/* Inline panel */}
      {panelOpen && (
        <div className="border border-grey-300 rounded-xl overflow-hidden bg-white shadow-sm">

          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 bg-darkblue-50 border-b border-darkblue-100">
            <div className="flex items-center gap-2">
              <DocumentMagnifyingGlassIcon className="w-4 h-4 text-darkblue-500" />
              <span className="text-xs font-bold uppercase tracking-wide text-darkblue-500">
                Document Extraction
              </span>
              {confidence != null && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  confidence >= 0.8 ? 'bg-green-100 text-green-700'
                  : confidence >= 0.5 ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-red-700'
                }`}>
                  {Math.round(confidence * 100)}% confidence
                </span>
              )}
            </div>
            {extractedAt && (
              <span className="text-xs text-slate-400">
                Extracted {new Date(extractedAt).toLocaleDateString()}
              </span>
            )}
          </div>

          <div className="p-4 space-y-3">

            {/* Loading */}
            {status === 'loading' && (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <div className="w-8 h-8 border-3 border-brand-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-darkblue-500 font-medium">Reading extraction data...</p>
              </div>
            )}

            {/* Error */}
            {status === 'error' && (
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Extraction unavailable</p>
                  <p className="text-xs text-red-600 mt-0.5">{errorMsg}</p>
                </div>
              </div>
            )}

            {/* Revealing / Ready */}
            {(status === 'revealing' || status === 'ready') && (
              <>
                {/* Progress bar during reveal */}
                {status === 'revealing' && totalCount > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>Reading fields...</span>
                      <span>{visibleCount} / {totalCount}</span>
                    </div>
                    <div className="h-1.5 bg-grey-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 rounded-full transition-all duration-300"
                        style={{ width: `${totalCount > 0 ? (visibleCount / totalCount) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* No fields extracted */}
                {status === 'ready' && totalCount === 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-slate-400">No mappable fields found in this document's extraction.</p>
                    {infoFields.length > 0 && (
                      <p className="text-xs text-slate-400 mt-1">See document info below.</p>
                    )}
                  </div>
                )}

                {/* Form fields */}
                {totalCount > 0 && (
                  <div className="space-y-2">
                    {FORM_FIELDS.map((fd) => {
                      const state = fieldStates[fd.formField];
                      if (!state) return null;
                      return (
                        <ExtractedFieldRow
                          key={fd.formField}
                          fieldDef={fd}
                          value={state.value}
                          accepted={state.accepted}
                          visible={state.visible}
                          onToggleAccept={(val) => toggleAccept(fd.formField, val)}
                          onValueChange={(val) => updateFieldValue(fd.formField, val)}
                        />
                      );
                    })}
                  </div>
                )}

                {/* Tags */}
                {extractedTags.length > 0 && status === 'ready' && (
                  <div className="p-3 bg-grey-50 border border-grey-200 rounded-lg">
                    <p className="text-xs font-bold uppercase tracking-wide text-darkblue-500 mb-2">
                      Extracted Tags
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {extractedTags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-brand-100 text-brand-600 text-xs font-medium rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">These will be merged with existing tags on apply.</p>
                  </div>
                )}

                {/* Info-only fields */}
                {infoFields.length > 0 && status === 'ready' && (
                  <div className="p-3 bg-grey-50 border border-grey-200 rounded-lg">
                    <p className="text-xs font-bold uppercase tracking-wide text-darkblue-500 mb-2">
                      Additional Info (read-only)
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {infoFields.map((f) => (
                        <div key={f.key}>
                          <p className="text-xs text-slate-400">{f.label}</p>
                          <p className="text-xs font-medium text-darkblue-500 truncate">{f.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Apply footer */}
                {status === 'ready' && totalCount > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-grey-200">
                    <p className="text-xs text-slate-400">
                      {acceptedCount} of {totalCount} field{totalCount !== 1 ? 's' : ''} selected
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          // Toggle all
                          const allAccepted = acceptedCount === totalCount;
                          setFieldStates((prev) => {
                            const next = {};
                            Object.entries(prev).forEach(([k, v]) => {
                              next[k] = { ...v, accepted: !allAccepted };
                            });
                            return next;
                          });
                        }}
                        className="text-xs text-slate-400 hover:text-darkblue-500 underline"
                      >
                        {acceptedCount === totalCount ? 'Deselect all' : 'Select all'}
                      </button>
                      <button
                        type="button"
                        onClick={handleApply}
                        disabled={acceptedCount === 0 && extractedTags.length === 0}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-darkblue-500 text-white rounded-lg hover:bg-darkblue-600 transition-colors disabled:opacity-40"
                      >
                        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                        Apply to Form
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ExtractionPanel;